import type { Article } from "./types";

export const article: Article = {
  slug: "how-to-stop-scrolling-at-night",
  title: "How to stop scrolling at night (without willpower)",
  h1: "How to stop scrolling at night",
  description:
    "The evening scroll is a habit with a trigger, not a character flaw. Here is the sequence that ends it, and the three mistakes that keep people stuck for months.",
  targets: [
    "how to stop scrolling at night",
    "stop doomscrolling before bed",
    "phone habits before sleep",
    "cant stop scrolling in bed",
  ],
  published: "2026-08-17",
  standfirst:
    "You did not decide to spend forty minutes there. That is the part worth paying attention to.",
  takeaways: [
    "The evening scroll starts in a gap, not in a decision — so willpower has nothing to act on. Change what is available in the gap instead.",
    "Block the trigger window rather than the whole day, and give the block an exit that is an action rather than a countdown.",
    "Decide what the gap is FOR before you take the phone out of it; an empty evening is a system nobody keeps.",
    "Charge the phone in another room last, not first — done first it is self-denial, done last it is the final step of something already working.",
  ],
  body: [
    {
      type: "p",
      text: "The evening scroll almost never begins with a decision. It begins with a gap — the dishes are done, the day is over, and there is a moment with nothing in it. The phone fills the gap before you have chosen anything, and forty minutes later you are still there, not enjoying it, wondering where the evening went.",
    },
    {
      type: "p",
      text: "That shape matters, because it tells you what will not work. You cannot out-decide a behaviour that never involved a decision. What you can do is change what is available in the gap.",
    },
    {
      type: "h2",
      id: "trigger",
      text: "Find the gap, not the app",
    },
    {
      type: "p",
      text: "For most people the trigger is one of four moments: sitting down after dinner, getting into bed, waking in the night, or the first minute of a task you are avoiding. Watch for two evenings and you will know which one is yours. You are looking for the moment before the phone is in your hand — that is the only place an intervention can go.",
    },
    {
      type: "note",
      text: "If you cannot name the moment, you are trying to fix the wrong thing. The app is downstream.",
    },
    {
      type: "h2",
      id: "sequence",
      text: "The sequence that works",
    },
    {
      type: "ol",
      items: [
        "Put a block on the gap, not on the day. One window — from when the trigger fires until you are asleep — is a rule you can actually keep. A daily quota is an accounting exercise and you will lose track of it by Wednesday.",
        "Give the block an exit that is a real action. Not a countdown, not a passcode you know: something small you have to complete. The block will be tested every single night, and a block that ends in an action survives that test in a way a block that ends in waiting does not.",
        "Decide what the gap is for. This is the step everyone skips. If the evening gap has no content, you are asking yourself to sit in an empty room and feel virtuous — nobody keeps that up. Scripture, a page of a book, a prayer, a conversation. Something that fills the same shape.",
        "Charge the phone in another room. Only after the first three. Done first, it is an act of self-denial that lasts four days; done last, it is the obvious final step of a system already working.",
      ],
    },
    {
      type: "h2",
      id: "mistakes",
      text: "The three mistakes that keep people stuck",
    },
    {
      type: "h3",
      text: "Blocking everything at once",
    },
    {
      type: "p",
      text: "Ten apps blocked from 6pm feels decisive and collapses within the week, because you will need one of them for something ordinary — a message, a map, a photo someone sent — and the fastest route to it is turning the whole system off. Two apps, one window. Add later, from a position of it working.",
    },
    {
      type: "h3",
      text: "Treating the first three days as data",
    },
    {
      type: "p",
      text: "The first evenings feel unreasonable. You will be certain the rule is too strict, and you will be wrong; that certainty is the habit arguing, and it is very fluent. Change nothing for two weeks. If it is still wrong then, it is actually wrong.",
    },
    {
      type: "h3",
      text: "Making it a punishment",
    },
    {
      type: "p",
      text: "A block framed as a penalty invites you to look for the loophole, and you will find one — it is your phone. A block framed as a door with a small cost is something you walk through several times a night without resentment, which is exactly why it is still there in a month.",
    },
    {
      type: "h2",
      id: "faith",
      text: "If you want the gap to be worth something",
    },
    {
      type: "p",
      text: "The evening gap is the most reliably free time most people have. It is also, for most of us, the time we most consistently give away without noticing. Christians have a long habit of naming that hour rather than losing it: compline, night prayer, the examen — a short, fixed thing at the end of the day.",
    },
    {
      type: "verse",
      verse: {
        text: "In peace I will both lay myself down and sleep, for you alone, Yahweh, make me live in safety.",
        ref: "Psalm 4:8",
      },
    },
    {
      type: "p",
      text: "This is what {Bible-Shield} is built around. The apps that take your evenings close at the hour you choose, and they open again after a short prayer written for the day you actually had. The block is not the product; the minute on the other side of it is. If you have tried the built-in limits and watched them stop working, {that-failure-has-a-structural-cause} rather than a personal one.",
      links: {
        "Bible-Shield": "/",
        "that-failure-has-a-structural-cause":
          "/blog/screen-time-limits-dont-work",
      },
    },
    {
      type: "p",
      text: "You do not have to use an app for any of this. You do have to put something in the gap, because the gap is not going away — and whatever is easiest to reach at 10:40pm is what will end up filling it.",
    },
    {
      type: "p",
      text: "One caveat worth stating before you change anything: the evening is not everybody's problem, and a fix aimed at the wrong hour is a fix that gets abandoned. {The-phone-and-faith-test} sorts that out in about four minutes — whether the nights are where your attention actually goes, or whether they are simply where you notice it.",
      links: { "The-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
  ],
  faq: [
    {
      q: "Does night mode or a greyscale screen help?",
      a: "A little, and less than people hope. Both make the phone less pleasant without making it less available, so they work for a few evenings and then stop being noticed. They are worth stacking on top of a block; they do not replace one.",
    },
    {
      q: "What if I need my phone at night for emergencies?",
      a: "Block apps, never the phone. Calls, messages and alarms should stay untouched — a system that could make you miss something real is one you will turn off entirely at the first scare, and then you have nothing.",
    },
    {
      q: "How long before it feels normal?",
      a: "About two weeks for the evening to stop feeling empty, in most people's experience of habit change. The first three days are the loudest and the least informative.",
    },
  ],
  related: ["screen-time-limits-dont-work", "phone-addiction-bible-verses"],
};
